import { useEffect, useRef, useState } from "react";
import { motion, useInView, useMotionValue, useSpring, useTransform } from "framer-motion";

/**
 * AnimatedCounter — smoothly counts up from 0 to `value` when scrolled into view.
 * Uses framer-motion springs for a luxe deceleration curve.
 */
export function AnimatedCounter({ value = 0, prefix = "", suffix = "", format = (n) => Math.round(n).toLocaleString(), duration = 1.6 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const mv = useMotionValue(0);
  const spring = useSpring(mv, { stiffness: 60, damping: 20, mass: 1.1 });
  const [display, setDisplay] = useState("0");
  useEffect(() => {
    if (inView) mv.set(value);
    const unsub = spring.on("change", (v) => setDisplay(format(v)));
    return () => unsub();
  }, [inView, value, mv, spring, format]);
  return (
    <span ref={ref}>
      {prefix}{display}{suffix}
    </span>
  );
}

/**
 * Reveal — fades-and-slides children into view once when scrolled to.
 */
export function Reveal({ children, delay = 0, y = 24, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}
